import React, { useEffect, useMemo, useState } from 'react';
import { api, apiError } from '../../lib/api';
import { MapPicker } from '../../components/MapPicker';

const STATUS_OPTS = [
  { value: 'planned',     label: 'Planned',      pill: 'pill-pending' },
  { value: 'completed',   label: 'Completed',    pill: 'pill-approved' },
  { value: 'no_response', label: 'No response',  pill: 'pill-rejected' },
  { value: 'follow_up',   label: 'Follow-up',    pill: 'pill-in_progress' },
];

export default function EmployeeVisits() {
  const [visits, setVisits] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [f, setF] = useState({
    business_id: '', status: 'completed', purpose: '', notes: '',
    latitude: '', longitude: '', photo_url: '', next_follow_up: '',
  });

  const load = async () => {
    try {
      const [v, b] = await Promise.all([api.get('/employee/visits'), api.get('/businesses')]);
      setVisits(v.data.visits || []);
      setBusinesses(b.data.businesses || []);
    } catch (e) { setErr(apiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const useMyLocation = () => {
    if (!navigator.geolocation) { setErr('Geolocation is not available.'); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => setF(prev => ({ ...prev, latitude: pos.coords.latitude.toFixed(6), longitude: pos.coords.longitude.toFixed(6) })),
      (e) => setErr('Location error: ' + e.message),
      { enableHighAccuracy: true, timeout: 8000 }
    );
  };

  const uploadPhoto = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const { data } = await api.post('/upload', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      setF(prev => ({ ...prev, photo_url: data.url }));
    } catch (e) { setErr(apiError(e, 'Upload failed')); }
    finally { setBusy(false); }
  };

  const submit = async (e) => {
    e.preventDefault(); setErr(''); setMsg(''); setBusy(true);
    try {
      await api.post('/employee/visits', {
        ...f,
        business_id: Number(f.business_id),
        latitude: f.latitude || undefined,
        longitude: f.longitude || undefined,
        next_follow_up: f.next_follow_up ? f.next_follow_up.replace('T',' ') + ':00' : undefined,
      });
      setMsg('Visit logged.');
      setF({ business_id: '', status: 'completed', purpose: '', notes: '', latitude: '', longitude: '', photo_url: '', next_follow_up: '' });
      await load();
    } catch (e) { setErr(apiError(e)); }
    finally { setBusy(false); }
  };

  const stats = useMemo(() => ({
    total: visits.length,
    completed: visits.filter(v => v.status === 'completed').length,
    followup: visits.filter(v => v.status === 'follow_up' || v.status === 'planned').length,
  }), [visits]);

  return (
    <div data-testid="employee-visits">
      <h2 className="mb-3">Field visits</h2>
      <div className="row g-3 mb-3">
        <div className="col-6 col-md-3"><div className="stat-card"><div className="stat-label">Total</div><div className="stat-value">{stats.total}</div></div></div>
        <div className="col-6 col-md-3"><div className="stat-card"><div className="stat-label">Completed</div><div className="stat-value">{stats.completed}</div></div></div>
        <div className="col-6 col-md-3"><div className="stat-card"><div className="stat-label">Follow-up / planned</div><div className="stat-value">{stats.followup}</div></div></div>
      </div>

      <div className="row g-3">
        <div className="col-lg-5">
          <div className="ap-card">
            <h4 className="mb-3">Log a new visit</h4>
            {err && <div className="alert alert-danger" data-testid="visit-err">{err}</div>}
            {msg && <div className="alert alert-success" data-testid="visit-msg">{msg}</div>}
            <form onSubmit={submit} data-testid="visit-form">
              <label className="form-label">Business</label>
              <select className="form-select mb-2" required value={f.business_id} onChange={e => setF({ ...f, business_id: e.target.value })} data-testid="visit-biz">
                <option value="">Pick a business…</option>
                {businesses.map(b => <option key={b.id} value={b.id}>{b.name} — {b.city_name}</option>)}
              </select>

              <label className="form-label">Status</label>
              <select className="form-select mb-2" value={f.status} onChange={e => setF({ ...f, status: e.target.value })} data-testid="visit-status">
                {STATUS_OPTS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>

              <label className="form-label">Purpose</label>
              <input className="form-control mb-2" placeholder="e.g. Onboarding meeting, plan upsell" value={f.purpose} onChange={e => setF({ ...f, purpose: e.target.value })} data-testid="visit-purpose" />

              <label className="form-label">Notes</label>
              <textarea className="form-control mb-2" rows={3} value={f.notes} onChange={e => setF({ ...f, notes: e.target.value })} data-testid="visit-notes" />

              <label className="form-label d-flex justify-content-between align-items-center">
                <span>GPS coordinates</span>
                <button type="button" className="btn-ap-outline btn-sm" onClick={useMyLocation} data-testid="visit-geo">📍 Use my location</button>
              </label>
              <div className="d-flex gap-2 mb-2">
                <input className="form-control" placeholder="Latitude" value={f.latitude} onChange={e => setF({ ...f, latitude: e.target.value })} data-testid="visit-lat"/>
                <input className="form-control" placeholder="Longitude" value={f.longitude} onChange={e => setF({ ...f, longitude: e.target.value })} data-testid="visit-lng"/>
              </div>
              <MapPicker latitude={f.latitude} longitude={f.longitude} onChange={({ latitude, longitude }) => setF({ ...f, latitude, longitude })} height={220} />

              <label className="form-label mt-3">Photo (optional)</label>
              <input type="file" className="form-control mb-2" accept="image/*" onChange={e => uploadPhoto(e.target.files?.[0])} data-testid="visit-photo"/>
              {f.photo_url && <img src={(process.env.REACT_APP_BACKEND_URL || '') + f.photo_url} alt="" style={{ width: 120, height: 120, objectFit: 'cover', borderRadius: 10 }} />}

              <label className="form-label mt-3">Next follow-up (optional)</label>
              <input type="datetime-local" className="form-control mb-3" value={f.next_follow_up} onChange={e => setF({ ...f, next_follow_up: e.target.value })} data-testid="visit-follow"/>

              <button className="btn-ap w-100" disabled={busy} data-testid="visit-submit">{busy ? 'Saving…' : 'Log visit'}</button>
            </form>
          </div>
        </div>

        <div className="col-lg-7">
          <div className="ap-card">
            <h4 className="mb-3">My visits</h4>
            {visits.length === 0 ? (
              <p className="text-muted-2 small">No visits yet — log your first one on the left.</p>
            ) : (
              <div className="d-flex flex-column gap-3">
                {visits.map(v => {
                  const s = STATUS_OPTS.find(x => x.value === v.status) || STATUS_OPTS[1];
                  return (
                    <div key={v.id} className="border rounded p-3" data-testid={`visit-${v.id}`}>
                      <div className="d-flex justify-content-between align-items-start">
                        <div>
                          <div className="fw-bold">{v.business_name}</div>
                          <div className="small text-muted-2">{v.business_address || '—'}</div>
                        </div>
                        <span className={'pill ' + s.pill}>{s.label}</span>
                      </div>
                      {v.purpose && <div className="small mt-2"><strong>Purpose:</strong> {v.purpose}</div>}
                      {v.notes && <div className="small">{v.notes}</div>}
                      <div className="small text-muted-2 mt-1">Logged {new Date(v.created_at).toLocaleString()}</div>
                      {v.next_follow_up && <div className="small">📅 Next follow-up: {new Date(v.next_follow_up).toLocaleString()}</div>}
                      <div className="d-flex gap-2 mt-2 flex-wrap">
                        {v.latitude && v.longitude && (
                          <a className="btn-ap-outline btn-sm" href={`https://maps.google.com/?q=${v.latitude},${v.longitude}`} target="_blank" rel="noreferrer" data-testid={`visit-map-${v.id}`}>View on map</a>
                        )}
                        {v.photo_url && <a className="btn-ap-outline btn-sm" href={(process.env.REACT_APP_BACKEND_URL || '') + v.photo_url} target="_blank" rel="noreferrer" data-testid={`visit-photo-${v.id}`}>View photo</a>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
